import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ExternalLink,
  Tag,
  Clock,
  Flame,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { fetchSaleItems, fetchFeedGenres } from "@/lib/api.ts";
import type { SaleCampaign, SaleItem } from "@/lib/api.ts";

export function SalePage() {
  const [campaigns, setCampaigns] = useState<SaleCampaign[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // 検索状態
  const [keyword, setKeyword] = useState("");
  const [genre, setGenre] = useState("");
  const [actress, setActress] = useState("");
  const [genres, setGenres] = useState<{ name: string; count: number }[]>([]);

  // 検索用の確定値（Enter/ボタン押下で確定）
  const [searchParams, setSearchParams] = useState<{
    q?: string;
    genre?: string;
    actress?: string;
  }>({});

  const doSearch = useCallback(() => {
    setSearchParams({
      q: keyword || undefined,
      genre: genre || undefined,
      actress: actress || undefined,
    });
  }, [keyword, genre, actress]);

  // ジャンル一覧取得
  useEffect(() => {
    fetchFeedGenres().then(setGenres).catch(() => {});
  }, []);

  // データ取得
  useEffect(() => {
    setLoading(true);
    fetchSaleItems(searchParams)
      .then((result) => {
        setCampaigns(result.data);
        setTotal(result.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [searchParams]);

  const hasFilters = !!(searchParams.q || searchParams.genre || searchParams.actress);

  const clearFilters = () => {
    setKeyword("");
    setGenre("");
    setActress("");
    setSearchParams({});
  };

  return (
    <div className="flex flex-col flex-1">
      {/* Hero + Search */}
      <div className="bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 px-4 md:px-8 lg:px-16 py-6 md:py-10">
        <div className="flex items-center gap-3 mb-3">
          <Flame size={28} className="text-red-500" />
          <h1 className="font-[var(--font-primary)] text-2xl md:text-3xl font-bold text-foreground m-0">
            セール中の作品
          </h1>
        </div>

        {/* Search Bar */}
        <div className="flex flex-col gap-2 mt-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="キーワード検索..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-card font-[var(--font-secondary)] text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <input
              type="text"
              placeholder="女優名..."
              value={actress}
              onChange={(e) => setActress(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              className="w-28 md:w-40 h-9 px-3 rounded-lg border border-border bg-card font-[var(--font-secondary)] text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              onClick={doSearch}
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity shrink-0"
            >
              検索
            </button>
          </div>

          {/* Genre Chips */}
          <div className="flex gap-1.5 flex-wrap">
            {genres.slice(0, 15).map((g) => (
              <button
                key={g.name}
                onClick={() => {
                  setGenre(genre === g.name ? "" : g.name);
                  setSearchParams((prev) => ({
                    ...prev,
                    genre: genre === g.name ? undefined : g.name,
                  }));
                }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  genre === g.name
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>

          {/* Active Filters */}
          {hasFilters && (
            <div className="flex items-center gap-2 mt-1">
              <span className="font-[var(--font-secondary)] text-xs text-muted-foreground">
                {loading ? "検索中..." : `${total}件ヒット`}
              </span>
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[11px] hover:bg-secondary/80 transition-colors"
              >
                <X size={10} />
                クリア
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center flex-1 py-20">
          <Loader2 size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-4 py-20">
          <Flame size={48} className="text-muted-foreground/30" />
          <p className="text-muted-foreground font-[var(--font-secondary)]">
            {hasFilters
              ? "検索条件に一致するセール作品がありません"
              : "現在セール中の作品はありません"}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              フィルターをクリア
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-8 py-6">
          {!hasFilters && (
            <p className="font-[var(--font-secondary)] text-sm text-muted-foreground px-4 md:px-8 lg:px-16 m-0">
              {campaigns.length}件のキャンペーン・{total}作品がセール中
            </p>
          )}
          {campaigns.map((campaign) => (
            <CampaignCarousel key={campaign.title} campaign={campaign} />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignCarousel({ campaign }: { campaign: SaleCampaign }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const updateButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 0);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateButtons();
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [updateButtons]);

  const scroll = (dir: "prev" | "next") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === "next" ? amount : -amount, behavior: "smooth" });
  };

  const endDate = new Date(campaign.end.replace(" ", "T"));
  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  const remaining =
    diffDays > 0
      ? `残り${diffDays}日${diffHours}時間`
      : diffHours > 0
        ? `残り${diffHours}時間`
        : "まもなく終了";

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-8 lg:px-16">
        <div className="flex items-center gap-2 min-w-0">
          <span className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-bold shrink-0">
            SALE
          </span>
          <h2 className="font-[var(--font-primary)] text-base font-bold text-foreground m-0 truncate">
            {campaign.title}
          </h2>
          <span className="font-[var(--font-secondary)] text-xs text-muted-foreground shrink-0">
            ({campaign.items.length})
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 text-orange-500">
            <Clock size={12} />
            <span className="font-[var(--font-secondary)] text-[11px] font-medium">
              {remaining}
            </span>
          </div>
          {/* Scroll Nav */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => scroll("prev")}
              disabled={!canScrollPrev}
              className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-secondary disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => scroll("next")}
              disabled={!canScrollNext}
              className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-secondary disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Scroll */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pl-4 md:pl-8 lg:pl-16 pr-4 md:pr-8 lg:pr-16 no-scrollbar snap-x snap-mandatory"
      >
        {campaign.items.map((item) => (
          <div
            key={item.id}
            className="flex-none w-[140px] sm:w-[160px] md:w-[180px] snap-start"
          >
            <SaleCard item={item} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SaleCard({ item }: { item: SaleItem }) {
  const hasDiscount =
    item.listPrice != null &&
    item.minPrice != null &&
    item.listPrice > item.minPrice;
  const discountPercent = hasDiscount
    ? Math.round((1 - item.minPrice! / item.listPrice!) * 100)
    : 0;

  return (
    <a
      href={item.affiliateUrl ?? "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-lg overflow-hidden bg-card border border-border shadow-sm hover:shadow-md transition-shadow no-underline"
    >
      <div className="w-full aspect-[2/3] overflow-hidden bg-secondary relative">
        {(item.thumbnailUrl || item.imageUrl) ? (
          <picture>
            {item.imageUrl && <source media="(min-width: 768px)" srcSet={item.imageUrl} />}
            <img
              src={item.thumbnailUrl || item.imageUrl!}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </picture>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/30 text-xs">
            No Image
          </div>
        )}
        {hasDiscount && (
          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-bold">
            -{discountPercent}%
          </span>
        )}
      </div>
      <div className="p-2 flex flex-col gap-1">
        <h4 className="font-[var(--font-secondary)] text-[11px] font-medium text-foreground line-clamp-2 leading-snug m-0">
          {item.title}
        </h4>

        {hasDiscount ? (
          <div className="flex items-center gap-1">
            <span className="font-[var(--font-secondary)] text-xs font-bold text-red-500">
              ¥{item.minPrice!.toLocaleString()}
            </span>
            <span className="font-[var(--font-secondary)] text-[9px] text-muted-foreground line-through">
              ¥{item.listPrice!.toLocaleString()}
            </span>
          </div>
        ) : item.minPrice != null ? (
          <span className="font-[var(--font-secondary)] text-[10px] text-muted-foreground">
            ¥{item.minPrice.toLocaleString()}~
          </span>
        ) : null}

        {item.genres.length > 0 && (
          <div className="flex gap-0.5 flex-wrap">
            {item.genres.slice(0, 2).map((g) => (
              <span
                key={g}
                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[8px]"
              >
                <Tag size={7} className="shrink-0" />
                {g}
              </span>
            ))}
          </div>
        )}

        <Link
          to={`/actress/${item.actress.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 no-underline group/actress"
        >
          {item.actress.imageUrl ? (
            <img
              src={item.actress.imageUrl}
              alt={item.actress.name}
              className="w-5 h-5 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[9px] text-muted-foreground shrink-0">
              {item.actress.name?.[0]}
            </div>
          )}
          <span className="font-[var(--font-secondary)] text-[10px] text-muted-foreground group-hover/actress:text-primary transition-colors truncate">
            {item.actress.name}
          </span>
        </Link>
      </div>
    </a>
  );
}
